import { useState } from 'react';
import { fetchJobsByTask, isOffQueueStatus } from '../../lib/aiJobs';
import { enqueueAiJobWithFiles } from '../../lib/aiJobAttachments';
import { fetchIncompleteChips, type IncompleteChip } from '../../lib/aiQueueTargets';
import { useUser } from '../../context/UserContext';
import { Button, SidePanel } from '../ui';
import TrelloCardDetailBody from '../comms/TrelloCardDetailBody';
import SpecQueueNoteFields from './SpecQueueNoteFields';
import { useAttachmentPicker } from './attachmentPicker';

const TASK_ONLY = '__task_description_only__';

interface SpecQueueButtonProps {
  taskId: string;
  taskTitle: string;
  onEnqueued?: () => void;
}

export default function SpecQueueButton({ taskId, taskTitle, onEnqueued }: SpecQueueButtonProps) {
  const { user } = useUser();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [chips, setChips] = useState<IncompleteChip[]>([]);
  const [choice, setChoice] = useState<string | null>(null);
  const [taskOnlyAvailable, setTaskOnlyAvailable] = useState(true);
  const [note, setNote] = useState('');
  const [model, setModel] = useState<string | null>(null);
  const attachments = useAttachmentPicker();

  const resetPicker = () => {
    setPickerOpen(false);
    setChoice(null);
    setNote('');
    setModel(null);
    attachments.clear();
    setError(null);
  };

  const enqueue = async (cardId: string | null, title: string) => {
    setBusy(true);
    setError(null);
    try {
      await enqueueAiJobWithFiles(
        {
          task_id: taskId,
          title,
          card_id: cardId,
          created_by: user?.id ?? null,
          queue_note: note.trim(),
          model,
        },
        attachments.files.map((f) => f.file)
      );
      resetPicker();
      onEnqueued?.();
    } catch (err) {
      console.error('Error enqueueing AI job:', err);
      setError(err instanceof Error ? err.message : 'Failed to enqueue spec');
    } finally {
      setBusy(false);
    }
  };

  const handleClick = async () => {
    if (busy) return;
    setError(null);
    setBusy(true);
    try {
      const [incomplete, jobs] = await Promise.all([
        fetchIncompleteChips(taskId),
        fetchJobsByTask(taskId),
      ]);
      const openJobs = jobs.filter((j) => !isOffQueueStatus(j.status));
      const queuedCards = new Set(openJobs.map((j) => j.card_id).filter(Boolean) as string[]);
      const taskOnlyOpen = openJobs.some((j) => !j.card_id);
      const available = incomplete.filter((c) => !queuedCards.has(c.cardId));
      const remainingTaskOnly = !taskOnlyOpen;
      const remainingCount = available.length + (remainingTaskOnly ? 1 : 0);

      if (remainingCount === 0) {
        setError('Everything on this task is already in the agent queues');
        return;
      }
      setChips(available);
      setTaskOnlyAvailable(remainingTaskOnly);
      if (remainingCount === 1) {
        setChoice(available.length === 1 ? available[0].cardId : TASK_ONLY);
      } else {
        setChoice(null);
      }
      setNote('');
      setModel(null);
      attachments.clear();
      setPickerOpen(true);
    } catch (err) {
      console.error('Error loading incomplete chips:', err);
      setError(err instanceof Error ? err.message : 'Failed to load comms chips');
    } finally {
      setBusy(false);
    }
  };

  const handleConfirm = async () => {
    if (!choice || busy) return;
    if (choice === TASK_ONLY) {
      await enqueue(null, taskTitle);
      return;
    }
    const chip = chips.find((c) => c.cardId === choice);
    if (!chip) return;
    await enqueue(chip.cardId, chip.cardName);
  };

  const options: Array<{ value: string; label: string; hint: string }> = [
    ...chips.map((chip) => ({
      value: chip.cardId,
      label: chip.cardName,
      hint: 'Bound to this comms chip',
    })),
    ...(taskOnlyAvailable
      ? [{
          value: TASK_ONLY,
          label: 'Task description only',
          hint: 'Spec from the task — not a comms card',
        }]
      : []),
  ];

  const busyLabel = attachments.files.length > 0 ? 'Uploading…' : 'Queuing…';

  return (
    <>
      <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start' }}>
        <Button
          size="sm"
          variant="secondary"
          icon="zap"
          onClick={handleClick}
          disabled={busy}
        >
          {busy ? 'Queuing…' : 'Spec Queue'}
        </Button>
        {error && !pickerOpen ? (
          <div
            style={{
              marginTop: '8px',
              padding: '8px 10px',
              borderRadius: 'var(--radius-md)',
              background: 'var(--status-down-soft)',
              border: '1px solid rgba(229, 72, 77, 0.25)',
              color: 'var(--danger-400)',
              fontSize: 'var(--text-sm)',
            }}
          >
            {error}
          </div>
        ) : null}
      </div>

      <SidePanel
        open={pickerOpen}
        onClose={() => {
          if (!busy) resetPicker();
        }}
        eyebrow="Agent Spec Queue"
        title="Choose one target"
        width={480}
        layer={1}
        footer={
          <>
            <Button onClick={handleConfirm} disabled={busy || !choice} icon="zap">
              {busy ? busyLabel : 'Confirm'}
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                if (!busy) resetPicker();
              }}
            >
              Cancel
            </Button>
          </>
        }
      >
        <p style={{ margin: '0 0 14px', fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>
          Already-queued items are hidden. Pick one remaining chip, or the task description only. The note and any attachments travel with the job for Owlbert.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {options.map((opt) => {
            const selected = choice === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                className="pressable"
                onClick={() => setChoice(opt.value)}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: '2px',
                  width: '100%',
                  padding: '12px 14px',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid ' + (selected ? 'var(--border-strong)' : 'var(--border-hairline)'),
                  background: selected ? 'var(--surface-active)' : 'var(--surface-raised)',
                  color: 'var(--text-body)',
                  cursor: 'pointer',
                  textAlign: 'left',
                  fontFamily: 'var(--font-ui)',
                }}
              >
                <span style={{ fontSize: 'var(--text-sm)', fontWeight: 500 }}>{opt.label}</span>
                <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>{opt.hint}</span>
              </button>
            );
          })}
        </div>
        <SpecQueueNoteFields
          note={note}
          onNoteChange={setNote}
          picker={attachments}
          disabled={busy}
          model={model}
          onModelChange={setModel}
          style={{ marginTop: '16px' }}
        />
        {error ? (
          <div
            style={{
              marginTop: '14px',
              padding: '10px 12px',
              borderRadius: 'var(--radius-md)',
              background: 'var(--status-down-soft)',
              border: '1px solid rgba(229, 72, 77, 0.25)',
              color: 'var(--danger-400)',
              fontSize: 'var(--text-sm)',
            }}
          >
            {error}
          </div>
        ) : null}

        {/* Context for the chosen chip — the card as Owlbert will read it */}
        {pickerOpen && choice && choice !== TASK_ONLY ? (
          <div style={{ marginTop: '18px' }}>
            <div className="t-eyebrow" style={{ marginBottom: '4px' }}>Card context</div>
            <p style={{ margin: '0 0 10px', fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>
              Owlbert reads this card from Trello when it picks up the job.
            </p>
            <TrelloCardDetailBody cardId={choice} />
          </div>
        ) : null}
      </SidePanel>
    </>
  );
}
